/* eslint-disable */
import { connect, useSelector } from 'react-redux';
import { useEffect, useMemo, useState, useCallback } from 'react';
import moment from 'moment';
import {
	addCommas,
	classes,
	filtersInfoChip,
	getHigherUnitAndQuantity,
	getHigherUnitName,
} from '@/Helpers';
import styles from './INVAnalysisListPage.module.scss';
import analyticsAction from '../../Actions/analytics.actions';
import { useAuth } from '../../Components/Auth/Login/auth';
import TableWithTabs from '../AIFMSUMMARY/TableWithTabs';

const COLMAP = {
	'Product Name': 'productName',
	Store: 'storeName',
	'Opening Stock Qty': 'openingStock',
	'Opening Stock Value (Rs)': 'openingStock',
	'Closing Stock Qty': 'closingStock',
	'Closing Stock Value (Rs)': 'closingStock',
	'Total Stocked In Qty': 'totalStockIn',
	'Total Stocked In Value (Rs)': 'totalStockIn',
	'Total Stocked Out Qty': 'totalStockOut',
	'Total Stocked Out Value (Rs)': 'totalStockOut',
	'Material Procured Qty': 'externalStockIn',
	'Material Procured Value (Rs)': 'externalStockIn',
	'Material Consumed Qty': 'recipeConsumption',
	'Material Consumed Value (Rs)': 'recipeConsumption',
	'Produced Qty': 'production',
	'Produced Value (Rs)': 'production',
	'Expired Qty': 'expired',
	'Expired Value (Rs)': 'expired',
	'Dispatched Qty': 'internalStockOut',
	'Dispatched Value (Rs)': 'internalStockOut',
	'Activity Date': 'stockDate',
};

const TABLE_COLUMNS = [
	{ title: 'Product Name', config: { type: 'DATA' } },
	{ title: 'Store', config: { type: 'DATA' } },
	{ title: 'Activity Date', config: { type: 'DATA' } },
	{ title: 'Opening Stock Qty', config: { type: 'DATA' } },
	{ title: 'Opening Stock Value (Rs)', config: { type: 'DATA' } },
	{ title: 'Closing Stock Qty', config: { type: 'DATA' } },
	{ title: 'Closing Stock Value (Rs)', config: { type: 'DATA' } },
	{ title: 'Total Stocked Out Qty', config: { type: 'DATA' } },
	{ title: 'Total Stocked Out Value (Rs)', config: { type: 'DATA' } },
	{ title: 'Material Procured Qty', config: { type: 'DATA' } },
	{ title: 'Material Procured Value (Rs)', config: { type: 'DATA' } },
	{ title: 'Produced Qty', config: { type: 'DATA' } },
	{ title: 'Produced Value (Rs)', config: { type: 'DATA' } },
	{ title: 'Total Stocked In Qty', config: { type: 'DATA' } },
	{ title: 'Total Stocked In Value (Rs)', config: { type: 'DATA' } },
	{ title: 'Material Consumed Qty', config: { type: 'DATA' } },
	{ title: 'Material Consumed Value (Rs)', config: { type: 'DATA' } },
	{ title: 'Expired Qty', config: { type: 'DATA' } },
	{ title: 'Expired Value (Rs)', config: { type: 'DATA' } },
	{ title: 'Dispatched Qty', config: { type: 'DATA' } },
	{ title: 'Dispatched Value (Rs)', config: { type: 'DATA' } },
];

const WEIGHT_WISE_COLUMNS = [
	'Product Name',
	'Store',
	'Activity Date',
	'Opening Stock Qty',
	'Material Procured Qty',
	'Produced Qty',
	'Total Stocked In Qty',
	'Material Consumed Qty',
	'Dispatched Qty',
	'Expired Qty',
	'Total Stocked Out Qty',
	'Closing Stock Qty',
];

const COUNT_UNITS = ['pc', 'count', 'piece', 'pieces', 'pcs'];

const isPcsUnit = (unit) => COUNT_UNITS.includes(unit?.toLowerCase());

const COST_WISE_COLUMNS = [
	'Product Name',
	'Store',
	'Activity Date',
	'Opening Stock Value (Rs)',
	'Material Procured Value (Rs)',
	'Produced Value (Rs)',
	'Total Stocked In Value (Rs)',
	'Material Consumed Value (Rs)',
	'Dispatched Value (Rs)',
	'Expired Value (Rs)',
	'Total Stocked Out Value (Rs)',
	'Closing Stock Value (Rs)',
];

const getRowStyles = (item) => {
	switch (item?.result) {
		default:
			return styles.normal;
	}
};

const INVAnalysisListPage = (props) => {
	const {
		data,
		setAnalyticsType,
		ANALYTICS_TYPE,
		filterData = {},
		loading = false,
		selectedTab = 'weight',
		setSelectedTab,
	} = props;

	const { appConfig = {} } = useAuth();
	const { measurement } = appConfig?.configs;
	const storeList = useSelector((state) => state?.form?.STORES?.data?.LIST);

	useEffect(() => {
		setAnalyticsType(ANALYTICS_TYPE, {}, false);
	}, []);

	const tableColumns = useMemo(() => {
		let columnNames;
		if (selectedTab === 'cost') {
			columnNames = COST_WISE_COLUMNS;
		} else {
			// 'weight' and 'count' tabs share the same columns
			columnNames = WEIGHT_WISE_COLUMNS;
		}

		if (props.format === 'productWise') {
			columnNames = columnNames.filter((col) => col !== 'Activity Date');
		}

		const STRING_COLUMNS = ['Product Name', 'Store', 'Activity Date'];
		return columnNames.map((colName, index) => ({
			key: colName,
			title: colName,
			fixed: index < 2 ? true : false,
			sortable: true,
			sortKey: COLMAP[colName] || colName,
			type: STRING_COLUMNS.includes(colName) ? 'string' : 'number',
		}));
	}, [selectedTab, props.format]);

	// Helper to render weight qty - for PCS items, show minWeight * pcsCount
	const renderWeightQty = (value, obj) => {
		if (value == null) return <div className='p-2 text-center'>-</div>;
		const unit = obj?.product?.unit ?? '';
		if (isPcsUnit(unit)) {
			const minWeight = obj?.productVariant?.minWeight;
			if (minWeight && minWeight > 0) {
				const weightInGrams = (value || 0) * minWeight;
				const [qty, higherUnit] = getHigherUnitAndQuantity(
					'g',
					weightInGrams
				);
				return (
					<div className='p-2 text-center'>{`${qty} ${higherUnit}`}</div>
				);
			}
			return <div className='p-2 text-center'>{`${value} pcs`}</div>;
		}
		const [qty, higherUnit] = getHigherUnitAndQuantity(unit, value);
		return <div className='p-2 text-center'>{`${qty} ${higherUnit}`}</div>;
	};

	// Helper to render count qty - show pcs and weight
	const renderCountQty = (value, obj) => {
		if (value == null) return <div className='p-2 text-center'>-</div>;
		const unit = obj?.product?.unit ?? '';
		if (isPcsUnit(unit)) {
			const minWeight = obj?.productVariant?.minWeight;
			const pcs = value || 0;
			if (minWeight && minWeight > 0) {
				const weightInGrams = pcs * minWeight;
				const [wQty, wUnit] = getHigherUnitAndQuantity(
					'g',
					weightInGrams
				);
				return (
					<div className='p-2 text-center'>
						<div>{`${pcs} pcs`}</div>
						<div className='text-xs text-gray-500'>{`${wQty} ${wUnit}`}</div>
					</div>
				);
			}
			return <div className='p-2 text-center'>{`${pcs} pcs`}</div>;
		}
		const [qty, higherUnit] = getHigherUnitAndQuantity(unit, value);
		return <div className='p-2 text-center'>{`${qty} ${higherUnit}`}</div>;
	};

	const tableRows = useMemo(() => {
		const rows = {};
		const allCols = [
			...new Set([...WEIGHT_WISE_COLUMNS, ...COST_WISE_COLUMNS]),
		];

		// Choose the appropriate qty renderer based on selected tab
		const renderQty =
			selectedTab === 'count' ? renderCountQty : renderWeightQty;

		const SPECIAL_ITEMS = {
			'Product Name': (_, obj) => {
				return (
					<div className='flex flex-col gap-1 p-2'>
						<p className='m-0 break-words overflow-hidden text-ellipsis'>
							{obj?.product?.productName +
								(obj?.productVariant?.displayName
									? ' (' +
									obj?.productVariant?.displayName +
									')'
									: '')}{' '}
							¦{' '}
							<span className='font-bold text-blue-500'>
								{getHigherUnitName(obj?.product?.unit)}
							</span>
						</p>
						<p className='text-xs text-gray-600 m-0 font-bold'>
							{obj?.productVariant?.clientVariantId}
						</p>
					</div>
				);
			},
			'Activity Date': (value) => {
				return (
					<div className='p-2 text-center whitespace-nowrap'>
						{value ? moment(value).format('DD-MM-YYYY') : '-'}
					</div>
				);
			},
			Store: (_, obj) => {
				const storeName = storeList?.find(
					(item) => item?.id === obj?.storeId
				)?.name;
				return <div className='p-2'>{storeName || '-'}</div>;
			},
			'Opening Stock Qty': (value, obj) => renderQty(value, obj),
			'Opening Stock Value (Rs)': (value, obj) => {
				if (value == null)
					return <div className='p-2 text-center'>-</div>;
				const unit = obj?.product?.unit ?? '';
				const [qty] = getHigherUnitAndQuantity(unit, value);
				const total = qty * (obj?.price ?? 0);
				return (
					<div className='p-2 text-center'>
						{addCommas(total, measurement, true)}
					</div>
				);
			},
			'Material Procured Qty': (value, obj) => renderQty(value, obj),
			'Material Consumed Qty': (value, obj) => renderQty(value, obj),
			'Total Stocked In Qty': (value, obj) => renderQty(value, obj),
			'Closing Stock Qty': (value, obj) => renderQty(value, obj),
			'Closing Stock Value (Rs)': (value, obj) => {
				if (value == null)
					return <div className='p-2 text-center'>-</div>;
				const unit = obj?.product?.unit ?? '';
				const [qty] = getHigherUnitAndQuantity(unit, value);
				const total = qty * (obj?.price ?? 0);
				return (
					<div className='p-2 text-center'>
						{addCommas(total, measurement, true)}
					</div>
				);
			},
			'Total Stocked Out Qty': (value, obj) => renderQty(value, obj),
			'Total Stocked Out Value (Rs)': (value, obj) => {
				if (value == null)
					return <div className='p-2 text-center'>-</div>;
				const unit = obj?.product?.unit ?? '';
				const [qty] = getHigherUnitAndQuantity(unit, value);
				const total = qty * (obj?.price ?? 0);
				return (
					<div className='p-2 text-center'>
						{addCommas(total, measurement, true)}
					</div>
				);
			},
			'Material Procured Value (Rs)': (value, obj) => {
				if (value == null)
					return <div className='p-2 text-center'>-</div>;
				const unit = obj?.product?.unit ?? '';
				const [qty] = getHigherUnitAndQuantity(unit, value);
				const total = qty * (obj?.price ?? 0);
				return (
					<div className='p-2 text-center'>
						{addCommas(total, measurement, true)}
					</div>
				);
			},
			'Total Stocked In Value (Rs)': (value, obj) => {
				if (value == null)
					return <div className='p-2 text-center'>-</div>;
				const unit = obj?.product?.unit ?? '';
				const [qty] = getHigherUnitAndQuantity(unit, value);
				const total = qty * (obj?.price ?? 0);
				return (
					<div className='p-2 text-center'>
						{addCommas(total, measurement, true)}
					</div>
				);
			},
			'Material Consumed Value (Rs)': (value, obj) => {
				if (value == null)
					return <div className='p-2 text-center'>-</div>;
				const unit = obj?.product?.unit ?? '';
				const [qty] = getHigherUnitAndQuantity(unit, value);
				const total = qty * (obj?.price ?? 0);
				return (
					<div className='p-2 text-center'>
						{addCommas(total, measurement, true)}
					</div>
				);
			},
			'Produced Qty': (value, obj) => renderQty(value, obj),
			'Produced Value (Rs)': (value, obj) => {
				if (value == null)
					return <div className='p-2 text-center'>-</div>;
				const unit = obj?.product?.unit ?? '';
				const [qty] = getHigherUnitAndQuantity(unit, value);
				const total = qty * (obj?.price ?? 0);
				return (
					<div className='p-2 text-center'>
						{addCommas(total, measurement, true)}
					</div>
				);
			},
			'Expired Qty': (value, obj) => renderQty(value, obj),
			'Expired Value (Rs)': (value, obj) => {
				if (value == null)
					return <div className='p-2 text-center'>-</div>;
				const unit = obj?.product?.unit ?? '';
				const [qty] = getHigherUnitAndQuantity(unit, value);
				const total = qty * (obj?.price ?? 0);
				return (
					<div className='p-2 text-center'>
						{addCommas(total, measurement, true)}
					</div>
				);
			},
			'Dispatched Qty': (value, obj) => {
				const val = (value || 0) + (obj?.internalStockOut || 0);
				return renderQty(val, obj);
			},
			'Dispatched Value (Rs)': (value, obj) => {
				const val = (value || 0) + (obj?.internalStockOut || 0);
				const unit = obj?.product?.unit ?? '';
				const [qty] = getHigherUnitAndQuantity(unit, val);
				const total = qty * (obj?.price ?? 0);
				return (
					<div className='p-2 text-center'>
						{addCommas(total, measurement, true)}
					</div>
				);
			},
		};

		allCols.forEach((colName) => {
			rows[colName] = (product) => {
				const value = COLMAP[colName]
					? product[COLMAP[colName]]
					: product[colName];
				product.price = (product.price == 0) ? product.productVariant?.costPrice : product.price;
				if (SPECIAL_ITEMS[colName]) {
					return SPECIAL_ITEMS[colName](value, product);
				}
				return <div className='p-2 text-center'>{value || '-'}</div>;
			};
		});

		return rows;
	}, [storeList, measurement, selectedTab]);

	const getTabInfo = useCallback(() => {
		return {
			tabList: [
				{ label: 'WEIGHT', value: 'weight' },
				{ label: 'COUNT', value: 'count' },
				{ label: 'COST', value: 'cost' },
			],
			tabValue: selectedTab,
			handleTabChange: setSelectedTab,
			activeTabColor: 'bg-yellow-400',
		};
	}, [selectedTab, setSelectedTab]);

	const sortInfo = useMemo(() => {
		const info = {};
		tableColumns.forEach((col) => {
			if (col.sortable) {
				info[col.key] = {
					value: col.sortKey,
					type: col.type,
				};
			}
		});
		return info;
	}, [tableColumns]);

	const getTableInfo = useCallback(() => {
		return {
			tableColumns: tableColumns,
			tableRows: tableRows,
			data: data || [],
			sortInfo,
			customCol: (col) => col.title,
		};
	}, [tableColumns, tableRows, data, sortInfo]);

	return (
		<div
			className={classes(
				styles.root,
				'list-page-root flex flex-col gap-2 p-2'
			)}
		>
			<div className='flex justify-between items-center mb-0'>
				{filtersInfoChip(
					filterData?.filtersInfo,
					filterData?.filtersValue
				)}
			</div>
			<div className='bg-white rounded-md border border-[#00000033]'>
				<TableWithTabs
					tableInfo={getTableInfo()}
					tabInfo={getTabInfo()}
					height={250}
					isLoading={loading}
					virtualization={true}
				/>
			</div>
		</div>
	);
};

const mapStateToProps = () => {
	return {};
};
const mapDispatchToProps = {
	setAnalyticsType: analyticsAction.setAnalyticsType,
};
export default connect(
	mapStateToProps,
	mapDispatchToProps
)(INVAnalysisListPage);
